/**
 * Local decryption utility - converts Java Decrypt class to JavaScript
 * Implements the same decryption logic as the Android app
 * 
 * Note: This requires the encryption key and IV to be provided
 * These should match the values used in the Android app
 */

import CryptoJS from 'crypto-js';

// Static key and IV from Android app (fallback values)
const STATIC_KEY = "54EDA16A7E700C51A98BE8F4C128646A";
const STATIC_IV = "7E226F48F6D37F74";

/**
 * Get key from string (similar to Encrypt.getKey in Java)
 * In Java, this likely converts the key string to a proper key format
 * For crypto-js, we can use the string directly or convert it
 * @param {string} keyString - The key string
 * @returns {string} - The key (crypto-js uses strings/WordArray)
 */
const getKey = (keyString) => {
    if (!keyString) {
        throw new Error('Key is required');
    }
    // crypto-js can work with string keys directly
    // But we might need to ensure it's the right length
    // For AES-256, key should be 32 bytes (256 bits)
    // crypto-js will handle the key derivation
    return keyString;
};

/**
 * Decrypt a string using AES
 * @param {string} stringToDecode - Base64 encoded encrypted string
 * @param {string} key - The encryption key
 * @param {string} iv - The initialization vector (null for ECB mode)
 * @returns {string} - Decrypted string
 */
const decrypt = (stringToDecode, key, iv) => {
    try {
        if (!key) {
            return null;
        }

        // Convert key to WordArray (crypto-js format)
        const keyWordArray = CryptoJS.enc.Utf8.parse(key);
        
        let decrypted;
        
        if (iv) {
            // AES/CBC/PKCS7Padding mode
            const ivWordArray = CryptoJS.enc.Utf8.parse(iv.substring(0, 16)); // IV must be 16 bytes
            
            // Decrypt using crypto-js
            decrypted = CryptoJS.AES.decrypt(stringToDecode, keyWordArray, {
                iv: ivWordArray,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
        } else {
            // AES/ECB/PKCS7Padding mode (no IV)
            // crypto-js supports ECB mode
            decrypted = CryptoJS.AES.decrypt(stringToDecode, keyWordArray, {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7
            });
        }
        
        // Convert decrypted WordArray to UTF-8 string
        return decrypted.toString(CryptoJS.enc.Utf8);
    } catch (error) {
        return null;
    }
};

/**
 * Encode function (similar to Encrypt.encode in Java)
 * This is used in the decryption process - it encrypts data
 * @param {string} data - Data to encode/encrypt
 * @param {string} key - The encryption key
 * @param {string} iv - The initialization vector
 * @returns {string} - Encrypted Base64 string
 */
const encode = (data, key, iv) => {
    try {
        // Convert key to WordArray (crypto-js format)
        const keyWordArray = CryptoJS.enc.Utf8.parse(key);
        const dataWordArray = CryptoJS.enc.Utf8.parse(data);
        
        let encrypted;
        
        if (iv) {
            // AES/CBC/PKCS7Padding mode
            const ivWordArray = CryptoJS.enc.Utf8.parse(iv.substring(0, 16)); // IV must be 16 bytes
            
            // Encrypt using crypto-js
            encrypted = CryptoJS.AES.encrypt(dataWordArray, keyWordArray, {
                iv: ivWordArray,
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
            });
        } else {
            // AES/ECB/PKCS7Padding mode (no IV)
            encrypted = CryptoJS.AES.encrypt(dataWordArray, keyWordArray, {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7
            });
        }
        
        // Return as Base64 string
        return encrypted.toString();
    } catch (error) {
        return null;
    }
};

/**
 * Final decryption function - implements the multi-step decryption process
 * @param {string} stringToDecode - The encrypted string to decode
 * @param {string} key - The main encryption key
 * @param {string} iv - The main initialization vector
 * @returns {string} - Decrypted string
 */
export const finalDecryption = (stringToDecode, key, iv) => {
    try {
        // PAS 1 -> extract first char
        const randomKeySize = stringToDecode.charAt(0);
        const size = randomKeySize.charCodeAt(0) - 65; // Convert char to int (A=65, so A-65=0)
        const step1 = stringToDecode.substring(1);

        // PAS 2 -> extract key and iv
        const randomKey = step1.substring(0, size);
        const randomIv = step1.substring(size, size + 16);

        const step2 = step1.substring(size + 16);

        // PAS 3 -> first decryption
        const encryptedKey = encode(randomKey, key, iv);
        if (!encryptedKey) {
            throw new Error('Failed to encode random key');
        }
        
        const first32Chars = encryptedKey.substring(0, 32);

        const step3 = decrypt(step2, first32Chars, randomIv);
        if (!step3) {
            throw new Error('Failed to decrypt step 2');
        }

        // PAS 4 -> extract 8 chars from the start of string and reverse
        let step4 = '';
        if (step3) {
            const step4half = step3.substring(8);
            // Reverse the string
            step4 = step4half.split('').reverse().join('');
        }

        // PAS 5 -> final decryption
        const finalStep = decrypt(step4, key, iv);
        return finalStep;
    } catch (error) {
        throw error;
    }
};

/**
 * Simple decrypt wrapper that tries finalDecryption first, then falls back to regular decrypt
 * Automatically gets key and IV from tokenStorage if not provided
 * @param {string} encryptedString - The encrypted string
 * @param {string} key - The encryption key (optional, will use tokenStorage if not provided)
 * @param {string} iv - The initialization vector (optional, will use tokenStorage if not provided)
 * @returns {string} - Decrypted string
 */
export const localDecrypt = (encryptedString, key = null, iv = null) => {
    // Import tokenStorage dynamically to avoid circular dependencies
    let encryptionKey = key;
    let encryptionIV = iv;
    
    if (!encryptionKey || !encryptionIV) {
        try {
            const { tokenStorage } = require('../app/tokenStorage');
            if (!encryptionKey) {
                encryptionKey = tokenStorage.getEncryptionKey();
            }
            if (!encryptionIV) {
                encryptionIV = tokenStorage.getEncryptionIV();
            }
        } catch (error) {
        }
    }
    
    // Use static key and IV as fallback if not provided
    if (!encryptionKey) {
        encryptionKey = STATIC_KEY;
    }
    
    if (!encryptionIV) {
        encryptionIV = STATIC_IV;
    }
    
    // Key must be provided - either as parameter, from tokenStorage, or static fallback
    if (!encryptionKey) {
        return null;
    }
    
    try {
        // Try finalDecryption first (multi-step process)
        return finalDecryption(encryptedString, encryptionKey, encryptionIV);
    } catch (error) {
        // Fallback to simple decrypt
        try {
            return decrypt(encryptedString, encryptionKey, encryptionIV);
        } catch (decryptError) {
            return null;
        }
    }
};

export default {
    finalDecryption,
    decrypt,
    encode,
    localDecrypt,
    getKey
};

